import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import EditablePage from "@/components/pagebuilder/EditablePage";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowRight, Phone, MapPin, Star, Quote, Heart } from "lucide-react";
import { useT } from "@/context/LanguageContext";
import { localizedContent } from "@/lib/localize";

const FALLBACK_HERO =
  "https://images.unsplash.com/photo-1773017825177-25acaa271258?crop=entropy&cs=srgb&fm=jpg&q=85";

// Hide an <img> gracefully if the hotlinked asset fails to load.
const hideOnError = (e) => {
  e.currentTarget.style.display = "none";
};

export default function Home() {
  const { lang } = useT();
  const [page, setPage] = useState(null);
  const [enquiry, setEnquiry] = useState({ name: "", email: "", phone: "" });
  const [enqBusy, setEnqBusy] = useState(false);
  const [enqDone, setEnqDone] = useState(false);
  const [enqErr, setEnqErr] = useState("");

  useEffect(() => {
    api.get("/cms/pages/home").then((r) => setPage(r.data)).catch(() => {});
  }, []);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!enquiry.name.trim()) { setEnqErr("Please enter your name."); return; }
    setEnqBusy(true); setEnqErr("");
    try {
      await api.post("/enquiries", {
        name: enquiry.name.trim(),
        email: enquiry.email.trim() || null,
        phone: enquiry.phone.trim() || null,
      });
      setEnqDone(true);
      setEnquiry({ name: "", email: "", phone: "" });
    } catch (err) {
      setEnqErr("Something went wrong. Please try again.");
    } finally {
      setEnqBusy(false);
    }
  };

  const c = localizedContent(page?.content, lang);
  const memorial = c.memorial || {};
  const gallery = c.gallery || [];
  const instructors = c.instructors || [];
  const testimonials = c.testimonials || [];

  return (
    <PublicLayout>
      <EditablePage slug="home" page={page} onSaved={setPage}>
      {/* Memorial banner */}
      {memorial.enabled && (
        <section className="bg-[var(--dojo-ink)] text-[var(--dojo-paper)]" data-testid="home-memorial">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex items-start gap-3 flex-1">
              <Heart size={16} className="mt-1 text-[var(--dojo-hinomaru)] shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--dojo-paper)]/60">
                  {memorial.prefix}
                </div>
                <div className="font-serif text-xl leading-tight">{memorial.name}</div>
                <p className="text-sm text-[var(--dojo-paper)]/70 mt-1 max-w-2xl leading-relaxed">
                  {memorial.message}
                </p>
              </div>
            </div>
            {memorial.cta_url && (
              <div className="shrink-0">
                <a
                  href={memorial.cta_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="home-memorial-cta"
                >
                  {memorial.cta_label || "Donate"}
                </a>
                {memorial.cta_note && (
                  <div className="text-[10px] text-[var(--dojo-paper)]/50 mt-2 text-center">
                    {memorial.cta_note}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src={c.hero_image || FALLBACK_HERO}
            alt=""
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = FALLBACK_HERO;
            }}
          />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 md:py-44 grid md:grid-cols-12 gap-8 text-[#FBFAF6]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-9"
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="hinomaru-dot" />
              <span className="text-[10px] uppercase tracking-[0.32em]">{c.tagline || "Traditional Karate · Chicagoland"}</span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight leading-[0.95] mb-8 max-w-4xl">
              {c.hero_headline || "Begin Your Journey"}
            </h1>
            <p className="text-lg md:text-xl max-w-2xl text-[#FBFAF6]/80 font-light leading-relaxed">
              {c.hero_sub}
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/contact" className="btn-primary" data-testid="home-cta-contact">
                Contact Us <ArrowRight size={14} className="inline ml-2" />
              </Link>
              <Link
                to="/about"
                className="btn-outline"
                style={{ color: "#FBFAF6", borderColor: "#FBFAF6" }}
                data-testid="home-cta-about"
              >
                About Us
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="md:col-span-3 flex md:justify-end items-start md:items-end"
          >
            <div className="font-kanji text-7xl md:text-8xl lg:text-9xl text-[var(--dojo-green)] leading-none" aria-hidden>
              {c.kanji || "空手道"}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Welcome + About */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dojo-ink-soft)] mb-4">{c.welcome_title || "Welcome!"}</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mb-4">
            {c.welcome_sub || "Come Join our class!"}
          </h2>
          <div className="brush-divider mb-6 max-w-xs" />
          <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--dojo-green)] mb-3">{c.about_heading || "About Us"}</div>
          <p className="text-base md:text-lg text-[var(--dojo-ink-soft)] leading-relaxed font-light">
            {c.about_body}
          </p>
          <Link to="/about" className="btn-outline mt-8 inline-flex" data-testid="home-about-more">
            More info <ArrowRight size={14} className="inline ml-2" />
          </Link>
        </div>
        <div className="md:col-span-6 order-1 md:order-2">
          {c.welcome_image && (
            <img
              src={c.welcome_image}
              alt="Karate practice"
              referrerPolicy="no-referrer"
              loading="lazy"
              onError={hideOnError}
              className="w-full aspect-[4/3] object-cover shadow-lg"
            />
          )}
        </div>
      </section>

      {/* Experience strip */}
      {c.experience_image && (
        <section className="relative overflow-hidden">
          <img
            src={c.experience_image}
            alt="Karate class in session"
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={hideOnError}
            className="w-full h-[420px] md:h-[520px] object-cover"
          />
          <div className="absolute inset-0 bg-black/45 flex items-center">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full text-[#FBFAF6]">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#FBFAF6]/70 mb-3">{c.experience_sub || "Our Dojo"}</div>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight max-w-2xl">
                {c.experience_title || "Come live the real experience"}
              </h2>
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {(c.show_gallery !== false) && gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dojo-ink-soft)] mb-3">Gallery</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-10">{c.gallery_heading || "Gallery"}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.map((g, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group relative overflow-hidden border border-[var(--dojo-border)]"
              >
                <img
                  src={g.url}
                  alt={g.caption || ""}
                  referrerPolicy="no-referrer"
                  loading="lazy"
                  onError={hideOnError}
                  className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {g.caption && (
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-[#FBFAF6] text-xs p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    {g.caption}
                  </figcaption>
                )}
              </motion.figure>
            ))}
          </div>
        </section>
      )}

      {/* Instructors */}
      {(c.show_instructors !== false) && instructors.length > 0 && (
        <section className="bg-[var(--dojo-paper-alt)] border-y border-[var(--dojo-border)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dojo-ink-soft)] mb-3">{c.instructors_eyebrow || "Meet our team"}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-10">{c.instructors_heading || "Instructors"}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {instructors.map((ins, i) => (
                <div key={i} className="bg-[var(--dojo-paper)] border border-[var(--dojo-border)] p-8 flex flex-col items-center text-center" data-testid={`home-instructor-${i}`}>
                  {ins.image && (
                    <img
                      src={ins.image}
                      alt={ins.name}
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      onError={hideOnError}
                      className="w-24 h-24 rounded-full object-cover border-2 border-[var(--dojo-green)] mb-5"
                    />
                  )}
                  <h3 className="font-serif text-xl mb-3">{ins.name}</h3>
                  {ins.location && (
                    <div className="flex items-center gap-2 text-sm text-[var(--dojo-ink-soft)] mb-1">
                      <MapPin size={14} className="text-[var(--dojo-green)]" /> {ins.location}
                    </div>
                  )}
                  {ins.phone && (
                    <a href={`tel:${ins.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 text-sm text-[var(--dojo-ink-soft)] hover:text-[var(--dojo-green)]">
                      <Phone size={14} className="text-[var(--dojo-green)]" /> {ins.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {(c.show_testimonials !== false) && testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--dojo-ink-soft)] mb-3">Reviews</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{c.reviews_heading || "Here is what our customers say"}</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-serif text-3xl">{c.reviews_rating || "5.0"}</span>
              <div className="flex">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={16} className="fill-[var(--dojo-green)] text-[var(--dojo-green)]" />
                ))}
              </div>
              <span className="text-sm text-[var(--dojo-ink-soft)]">· {c.reviews_count || "reviews"}</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((tm, i) => (
              <motion.blockquote
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="bg-[var(--dojo-paper-alt)] border border-[var(--dojo-border)] p-7"
              >
                <Quote size={22} className="text-[var(--dojo-green)] mb-4" />
                <p className="text-[var(--dojo-ink)] leading-relaxed mb-5 font-light">{tm.text}</p>
                <footer className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[var(--dojo-green)] text-[#FBFAF6] flex items-center justify-center font-serif">
                    {(tm.name || "?").charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{tm.name}</div>
                    <div className="text-xs text-[var(--dojo-ink-soft)]">{tm.when}</div>
                  </div>
                </footer>
              </motion.blockquote>
            ))}
          </div>
        </section>
      )}

      {/* Begin Your Journey — enquiry form */}
      <section className="text-[#FBFAF6]" style={{ background: "var(--dojo-maroon-dark)" }} data-testid="home-enquiry">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 md:py-24 text-center">
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-3">Begin your Journey!</h2>
          <p className="text-[#FBFAF6]/75 font-light mb-10">
            Leave your details and we&apos;ll reach out about joining a class.
          </p>
          {enqDone ? (
            <div className="bg-white/10 border border-white/25 p-8" data-testid="enquiry-success">
              <p className="text-lg">Thank you! We&apos;ve received your enquiry and will be in touch soon.</p>
              <button className="btn-outline mt-6" style={{ color: "#FBFAF6", borderColor: "#FBFAF6" }} onClick={() => setEnqDone(false)}>
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submitEnquiry} className="space-y-4 text-left" data-testid="enquiry-form">
              <input
                className="w-full px-4 py-3 bg-white text-[var(--dojo-ink)] placeholder-[var(--dojo-ink-soft)] border border-white/20 focus:outline-none"
                placeholder="Name"
                value={enquiry.name}
                onChange={(e) => setEnquiry((q) => ({ ...q, name: e.target.value }))}
                data-testid="enquiry-name"
              />
              <input
                type="email"
                className="w-full px-4 py-3 bg-white text-[var(--dojo-ink)] placeholder-[var(--dojo-ink-soft)] border border-white/20 focus:outline-none"
                placeholder="Email"
                value={enquiry.email}
                onChange={(e) => setEnquiry((q) => ({ ...q, email: e.target.value }))}
                data-testid="enquiry-email"
              />
              <input
                className="w-full px-4 py-3 bg-white text-[var(--dojo-ink)] placeholder-[var(--dojo-ink-soft)] border border-white/20 focus:outline-none"
                placeholder="Phone"
                value={enquiry.phone}
                onChange={(e) => setEnquiry((q) => ({ ...q, phone: e.target.value }))}
                data-testid="enquiry-phone"
              />
              {enqErr && <div className="text-sm text-[#FFD9DC]" data-testid="enquiry-error">{enqErr}</div>}
              <div className="text-center pt-2">
                <button
                  type="submit"
                  disabled={enqBusy}
                  className="btn-primary"
                  style={{ background: "#FBFAF6", color: "var(--dojo-maroon)", borderColor: "#FBFAF6" }}
                  data-testid="enquiry-submit"
                >
                  {enqBusy ? "Submitting…" : "Submit"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* CTA strip */}
      <section className="text-[#FBFAF6]" style={{ background: "var(--dojo-maroon)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#FBFAF6]/70 mb-3">Begin your practice</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Step onto the tatami.</h2>
          </div>
          <div className="flex gap-4">
            <Link
              to="/contact"
              className="btn-outline"
              style={{ color: "#FBFAF6", borderColor: "#FBFAF6" }}
              data-testid="home-cta-visit"
            >
              Contact a Dojo
            </Link>
          </div>
        </div>
      </section>
      </EditablePage>
    </PublicLayout>
  );
}
